// components/DepartureDetailModal.jsx
import React, { useEffect } from 'react';
import { formatTime, formatDelayHuman, calculateTimeRemainingDetailed } from '../utils/time';
import { getFullHeadsign } from '../utils/headsigns';

/**
 * Full-screen modal showing complete departure details.
 * Triggered by clicking a departure row (DepartureCard or StopDeparturesPopup).
 */
function DepartureDetailModal({ departure, onClose }) {
    // Close on Escape key — must be before any early returns (React hooks rules)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    if (!departure) return null;

    const delayInSeconds = departure.delayInSeconds;

    // Determine delay status class
    const getStatusClass = () => {
        if (delayInSeconds === null || delayInSeconds === undefined) return 'detail-on-time';
        if (Math.abs(delayInSeconds) < 60) return 'detail-on-time';
        if (delayInSeconds > 0) return 'detail-late';
        return 'detail-early';
    };

    const getStatusLabel = () => {
        if (delayInSeconds === null || delayInSeconds === undefined) return 'Punktualnie';
        if (Math.abs(delayInSeconds) < 60) return 'Punktualnie';
        if (delayInSeconds > 0) return 'Opóźniony';
        return 'Przed czasem';
    };

    return (
        <div className="departure-detail-overlay" onClick={onClose}>
            <div className="departure-detail-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="detail-header">
                    <div className="detail-line-badge">{departure.routeShortName}</div>
                    <button className="detail-close-btn" onClick={onClose} aria-label="Zamknij szczegóły">
                        &times;
                    </button>
                </div>

                {/* Direction */}
                <div className="detail-direction">
                    <span className="detail-label">Kierunek</span>
                    <span className="detail-value">{getFullHeadsign(departure.headsign) || 'brak danych'}</span>
                </div>

                {/* Info grid */}
                <div className="detail-grid">
                    {/* Scheduled time */}
                    <div className="detail-item">
                        <span className="detail-label">🕐 Rozkładowy</span>
                        <span className="detail-value">{formatTime(departure.theoreticalTime)}</span>
                    </div>

                    {/* Estimated time */}
                    <div className="detail-item">
                        <span className="detail-label">🕑 Przewidywany</span>
                        <span className="detail-value detail-value-accent">{formatTime(departure.estimatedTime)}</span>
                    </div>

                    {/* Delay */}
                    <div className="detail-item">
                        <span className="detail-label">⏱ Opóźnienie</span>
                        <span className={`detail-value ${getStatusClass()}`}>
                            {(delayInSeconds !== null && delayInSeconds !== undefined)
                                ? formatDelayHuman(delayInSeconds)
                                : '—'}
                        </span>
                    </div>

                    {/* Status */}
                    <div className="detail-item">
                        <span className="detail-label">📋 Status</span>
                        <span className={`detail-status-badge ${getStatusClass()}`}>
                            {getStatusLabel()}
                        </span>
                    </div>
                </div>

                {/* Countdown */}
                <div className="detail-countdown-section">
                    <span className="detail-label">Odjazd za</span>
                    <span className="detail-countdown-value">
                        {calculateTimeRemainingDetailed(departure.estimatedTime)}
                    </span>
                </div>

            </div>
        </div>
    );
}

export default DepartureDetailModal;
